import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Heading, Text, Button, useToast, Spinner, Divider,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  useDisclosure, SimpleGrid, Tabs, TabList, Tab, TabPanels, TabPanel,
  VStack, HStack, Input, Checkbox, Code
} from '@chakra-ui/react';
import axios from 'axios';

const API_BASE_URL = 'https://api-amg2dwk5ga-uc.a.run.app';

// Reusable component to display a user's key-value details
const UserDetail = ({ label, value }) => (
  <Box>
    <Text fontSize="sm" color="gray.500" casing="uppercase">
      {label}
    </Text>
    <Text fontWeight="medium" fontSize="md">{value || 'N/A'}</Text>
  </Box>
);

// Reusable component to render a list of members
const MemberList = ({ members, onApprove, onReject, onViewDetails,onDelete, status }) => (
  <VStack divider={<Divider />} spacing={4} align="stretch">
    {members.length === 0 ? (
      <Text p={2}>No members found with '{status}' status.</Text>
    ) : (
      members.map((member) => (
        <HStack key={member.id} justify="space-between" p={2} borderRadius="md" _hover={{ bg: 'gray.50' }}>
          <Box>
            <Text fontWeight="bold">{member.name} {member.member_id && <Text as="span" fontSize="sm" color="gray.500">({member.member_id})</Text>}</Text>
            <Text fontSize="sm">{member.phone_number} | From: {member.origin_place}</Text>
          </Box>
          <HStack>
            <Button colorScheme="blue" size="sm" onClick={() => onViewDetails(member)}>View Details</Button>
            {status === 'pending' && (
              <>
                <Button colorScheme="green" size="sm" onClick={() => onApprove(member.id)}>Approve</Button>
                <Button colorScheme="red" size="sm" onClick={() => onReject(member.id)}>Reject</Button>
              </>
            )}
            {(status === 'pending' || status === 'rejected') && (
                <Button colorScheme="gray" variant="outline" size="sm" onClick={() => onDelete(member.id, member.name)}>Delete</Button>
            )}
          </HStack>
        </HStack>
      ))
    )}
  </VStack>
);

const BusinessListAdmin = ({ businesses, onApprove, onReject, onToggleFeature, onTogglePremium, status }) => (
  <VStack divider={<Divider />} spacing={4} align="stretch">
    {businesses.length === 0 ? <Text p={2}>No businesses found with '{status}' status.</Text> : businesses.map((business) => (
      <HStack key={business.id} justify="space-between" p={2} borderRadius="md" _hover={{ bg: 'gray.50' }}>
        <Box>
          <Text fontWeight="bold">{business.business_name}</Text>
          <Text fontSize="sm">Owner: {business.owner_name}</Text>
        </Box>
        <HStack>
          {status === 'pending' && (
            <>
              <Button size="sm" colorScheme="green" onClick={() => onApprove(business.id)}>Approve</Button>
              <Button size="sm" colorScheme="red" onClick={() => onReject(business.id)}>Reject</Button>
            </>
          )}
          {status === 'approved' && (
            <HStack>
              <Checkbox isChecked={business.is_premium} onChange={() => onTogglePremium(business.id)}>
                Premium
              </Checkbox>
              <Checkbox isChecked={business.is_featured} onChange={() => onToggleFeature(business.id)}>
                Show on Homepage
              </Checkbox>
            </HStack>
          )}
        </HStack>
      </HStack>
    ))}
  </VStack>
);

const AdminDashboard = () => {
  // State for member lists
  const [pendingMembers, setPendingMembers] = useState([]);
  const [approvedMembers, setApprovedMembers] = useState([]);
  const [rejectedMembers, setRejectedMembers] = useState([]);
  const [allBusinesses, setAllBusinesses] = useState([]); 
  
  // State for matrimony search
  const [searchInput, setSearchInput] = useState('');
  const [searchedProfile, setSearchedProfile] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const toast = useToast();
  
  const { isOpen: isUserModalOpen, onOpen: onUserModalOpen, onClose: onUserModalClose } = useDisclosure();
  const { isOpen: isMatrimonyModalOpen, onOpen: onMatrimonyModalOpen, onClose: onMatrimonyModalClose } = useDisclosure();

  // Fetches all admin data using the callApi helper
  const fetchAdminData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [pendingData, approvedData, rejectedData,businessesData] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/pending_members`),
        axios.get(`${API_BASE_URL}/api/admin/approved_members`),
        axios.get(`${API_BASE_URL}/api/admin/rejected_members`),
        axios.get(`${API_BASE_URL}/api/admin/businesses`),
      ]);
      setPendingMembers(pendingData.data);
      setApprovedMembers(approvedData.data);
      setRejectedMembers(rejectedData.data);
      setAllBusinesses(businessesData.data);
    } catch (error) {
      toast({ title: 'Error fetching members', description: error.message, status: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  const pendingBusinesses = useMemo(() => allBusinesses.filter(b => b.status === 'pending'), [allBusinesses]);
  const approvedBusinesses = useMemo(() => allBusinesses.filter(b => b.status === 'approved'), [allBusinesses]);


  const handleAction = async (actionPromise) => {
    try {
      const response = await actionPromise;
      toast({ title: 'Success', description: response.data.message, status: 'success' });
      fetchAdminData(); // Refresh all lists after an action
    } catch (error) {
      toast({ title: 'Action Failed', description: error.message, status: 'error' });
    }
  };

  const handleApprove = (userId) => {
    handleAction(axios.post(`${API_BASE_URL}/api/admin/approve_member/${userId}`));
  };
  
  const handleReject = (userId) => {
    // This assumes you have a /api/admin/reject_member/<id> route in your Python function
    handleAction(axios.post(`${API_BASE_URL}/api/admin/reject_member/${userId}`));
  };
  const handleDelete = async (userId, userName) => {
    // 1. Add a confirmation dialog to prevent accidental deletion
    if (window.confirm(`Are you sure you want to permanently delete the profile for "${userName}"? This action cannot be undone.`)) {
      handleAction(axios.post(`${API_BASE_URL}/api/admin/reject_member/${userId}`));
    }
  };

  const handleApproveBusiness = (businessId) => handleAction(axios.post(`${API_BASE_URL}/api/admin/approve_business/${businessId}`));
  const handleRejectBusiness = (businessId) => handleAction(axios.post(`${API_BASE_URL}/api/admin/reject_business/${businessId}`));
  const handleToggleFeature = (businessId) => handleAction(axios.post(`${API_BASE_URL}/api/admin/toggle_feature_business/${businessId}`));
  const handleTogglePremium = (businessId) => handleAction(axios.post(`${API_BASE_URL}/api/admin/toggle_premium_business/${businessId}`));


  
  const handleViewDetails = (user) => {
    setSelectedUser(user);
    onUserModalOpen();
  };

  const handleMatrimonySearch = async () => {
    if (!searchInput.trim()) return;
    setIsSearching(true);
    try {
      const responseData = await axios.get(`${API_BASE_URL}/api/admin/matrimony_profile/${searchInput.trim()}`);
      setSearchedProfile(responseData.data);
      console.log("Printing response data ",responseData);
      onMatrimonyModalOpen();
    } catch (error) {
      toast({ title: 'Search Failed', description: error.message, status: 'error'});
    } finally {
      setIsSearching(false);
    }
  };

  const handleMarkAsMarried = async () => {
    if (!searchedProfile || !searchedProfile.user_id) return;

    // Add a confirmation dialog
    if (!window.confirm("Are you sure you want to mark this member as married? This action cannot be undone from this panel.")) {
      return;
    }

    try {
      
      await axios.post(
        `${API_BASE_URL}/api/admin/mark_as_married/${searchedProfile.user_id}`);

      toast({ title: 'Success', description: 'Member marked as married.', status: 'success' });
      // Update the state locally so the checkbox checks itself
      setSearchedProfile(prev => ({ ...prev, is_married: true }));
      // You can also close the modal here if you want
      // onMatrimonyModalClose();

    } catch (error) {
      toast({ 
        title: 'Action Failed', 
        description: error.response?.data?.message || 'An error occurred', 
        status: 'error' 
      });
    }
  };

  return (
    <>
      <Box p={8} maxW="1200px" mx="auto">
        <Heading mb={6}>Admin Dashboard</Heading>
        
        <Box borderWidth={1} borderRadius="lg" p={4} mb={8}>
            <Heading size="md" mb={4}>Search Matrimony Profile</Heading>
            <HStack>
                <Input 
                    placeholder="Enter Member ID (e.g., BG...)" 
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                />
                <Button colorScheme="brand" onClick={handleMatrimonySearch} isLoading={isSearching}>Search</Button>
            </HStack>
        </Box>

        <Box borderWidth={1} borderRadius="lg" p={4}>
            <Tabs isFitted variant="enclosed-colored">
                <TabList mb="1em">
                    <Tab _selected={{ color: 'white', bg: 'blue.500' }}>Pending ({pendingMembers.length})</Tab>
                    <Tab _selected={{ color: 'white', bg: 'green.500' }}>Approved ({approvedMembers.length})</Tab>
                    <Tab _selected={{ color: 'white', bg: 'red.500' }}>Rejected ({rejectedMembers.length})</Tab>
                </TabList>
                <TabPanels>
                    <TabPanel>
                        {isLoading ? <Spinner /> : <MemberList status="pending" members={pendingMembers} onViewDetails={handleViewDetails} onApprove={handleApprove} onReject={handleReject} onDelete={handleDelete} />}
                    </TabPanel>
                    <TabPanel>
                        {isLoading ? <Spinner /> : <MemberList status="approved" members={approvedMembers} onViewDetails={handleViewDetails} />}
                    </TabPanel>
                    <TabPanel>
                        {isLoading ? <Spinner /> : <MemberList status="rejected" members={rejectedMembers} onViewDetails={handleViewDetails} onDelete={handleDelete} />}
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Box>
      </Box>

      {/* User Details Modal */}
      <Modal isOpen={isUserModalOpen} onClose={onUserModalClose} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Member Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedUser && (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                <UserDetail label="Member ID" value={selectedUser.member_id} />
                <UserDetail label="Full Name" value={selectedUser.name} />
                <UserDetail label="Phone Number" value={selectedUser.phone_number} />
                <UserDetail label="Age" value={selectedUser.age} />
                <UserDetail label="Date of Birth" value={selectedUser.date_of_birth} />
                <UserDetail label="Gender" value={selectedUser.gender} />
                <UserDetail label="Marital Status" value={selectedUser.marital_status} />
                <UserDetail label="Gotra" value={selectedUser.gotra} />
                <UserDetail label="Occupation" value={selectedUser.occupation} />
                <UserDetail label="Father's Name" value={selectedUser.father_name} />
                <UserDetail label="Mother's Name" value={selectedUser.mother_name} />
                <UserDetail label="Native Place" value={selectedUser.origin_place} />
                <UserDetail label="Registration Date" value={selectedUser.created_at} />
              </SimpleGrid>
            )}
          </ModalBody>
          <ModalFooter><Button colorScheme="gray" onClick={onUserModalClose}>Close</Button></ModalFooter>
        </ModalContent>
      </Modal>
      <Box borderWidth={1} borderRadius="lg" p={4}>
        <Heading size="lg" mb={4}>Business Management</Heading>
        <Tabs isFitted variant="enclosed-colored">
          <TabList>
            <Tab _selected={{ color: 'white', bg: 'purple.500' }}>Pending Businesses ({pendingBusinesses.length})</Tab>
            <Tab _selected={{ color: 'white', bg: 'teal.500' }}>Approved Businesses ({approvedBusinesses.length})</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              {isLoading ? <Spinner /> : <BusinessListAdmin status="pending" businesses={pendingBusinesses} onApprove={handleApproveBusiness} onReject={handleRejectBusiness} />}
            </TabPanel>
            <TabPanel>
              {isLoading ? <Spinner /> : <BusinessListAdmin status="approved" businesses={approvedBusinesses} onToggleFeature={handleToggleFeature} onTogglePremium={handleTogglePremium}/>}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
      
      {/* Matrimony Details Modal */}
      <Modal isOpen={isMatrimonyModalOpen} onClose={onMatrimonyModalClose} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Full Matrimony Profile (Admin View)</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {searchedProfile && (
              <VStack spacing={4} align="stretch">
                {/* DEBUGGING TIP: Uncomment the line below to see the raw data object */}
                {/* <Code p={4} borderRadius="md" w="full"><pre>{JSON.stringify(searchedProfile, null, 2)}</pre></Code>*/}

                <SimpleGrid columns={2} spacing={5}>
                  <UserDetail label="Member ID" value={searchedProfile.member_id} />
                  <UserDetail label="Name" value={searchedProfile.name} />
                  <UserDetail label="Contact Phone Number" value={searchedProfile.phone_number} />
                  <UserDetail label="Father's Name" value={searchedProfile.father_name} />
                  <UserDetail label="Mother's Name" value={searchedProfile.mother_name} />
                  <UserDetail label="Education" value={searchedProfile.education} />
                  <UserDetail label="Current Location" value={searchedProfile.current_location} />
                  <UserDetail label="Date of Birth" value={searchedProfile.date_of_birth} />
                  <UserDetail label="Height" value={searchedProfile.height} />
                  <UserDetail label="Complexion" value={searchedProfile.complexion} />
                  <UserDetail label="Gotra" value={searchedProfile.gotra} />
                  <UserDetail label="Mother's Gotra" value={searchedProfile.mother_gotra} />
                  <UserDetail label="Occupation" value={searchedProfile.occupation} />
                  <UserDetail label="Income" value={searchedProfile.income} />
                  <UserDetail label="Work Location" value={searchedProfile.work_location} />
                  <UserDetail label="Father's Occupation" value={searchedProfile.father_occupation} />
                  <UserDetail label="Siblings" value={searchedProfile.siblings} />
                  <UserDetail label="Preferences" value={searchedProfile.preferences} />
                </SimpleGrid>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Checkbox
              colorScheme="green"
              isChecked={searchedProfile?.is_married}
              onChange={handleMarkAsMarried}
              isDisabled={searchedProfile?.is_married} // Disables checkbox if already checked
              mr="auto" // Pushes it to the left
            >
              Mark as Married
            </Checkbox>
            <Button colorScheme="gray" onClick={onMatrimonyModalClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AdminDashboard;